// @ts-strict
import { Injectable, NgZone } from "@angular/core";
import {
  BehaviorSubject,
  catchError,
  Observable,
  shareReplay,
  Subject,
  takeUntil,
  throwError,
} from "rxjs";
import { HttpClient } from "@angular/common/http";
import { LMLoggerService } from "@services/lm-logger.service";
import {
  LMBusyIndicatorService,
  LMFormatUtilService,
  LMUrlUtilService,
} from "@app/services";
import { SohoModalDialogRef, SohoModalDialogService } from "ids-enterprise-ng";
import { LMDialogButtonKey, LMUniqueIdService } from "@app/utils";
import { LMActionPickerComponent } from "./lm-action-picker.component";

interface LMActionSpecResponse {
  actions: LMActionSpec[];
  status: string;
}

@Injectable()
export class LMActionPickerService {
  // used internally for destroying the cache$
  private destroyer$ = new Subject<void>();
  private actionSpecsSource = new BehaviorSubject<LMActionSpec[]>([]);
  // for the action picker component to subscribe to, to signal that the action spec is already retrieved and avoid ngAfterViewInit error
  public actionSpecs$ = this.actionSpecsSource.asObservable();
  // used to cache the result so we don't have to retrieve the same request over and over again on the same component (consumer) instance
  private cache$!: Observable<LMActionSpecResponse>;

  private previousUrl!: string;

  constructor(
    private readonly http: HttpClient,
    private zone: NgZone,
    private readonly lmLoggerService: LMLoggerService,
    private readonly LMUrlUtilService: LMUrlUtilService,
    private readonly modalService: SohoModalDialogService,
    private readonly lmBusyIndicatorService: LMBusyIndicatorService,
    private readonly lmUniqueIdService: LMUniqueIdService,
    private readonly lmFormatUtilService: LMFormatUtilService
  ) {}

  public getActionSpecs(businessClass: string, listName: string, type?: ReadonlyArray<LMActionPickerActionType>, excludedActions?: ReadonlyArray<string>) {
    this.cache$ = this.requestActionSpec(businessClass, listName)
    .pipe(
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.cache$
      .pipe(takeUntil(this.destroyer$))
      .subscribe((actionSpecs: LMActionSpecResponse) => {
        if (actionSpecs?.status === "COMPLETED") {
          let actions = actionSpecs?.actions || [];

          if (Array.isArray(type)) {
            actions = actions.filter(action => type?.includes(action?.type as LMActionPickerActionType));
          }

          if (Array.isArray(excludedActions)) {
            actions = actions.filter(action => !excludedActions?.includes(action?.name || ''));
          }

          this.actionSpecsSource.next(actions);
        }
      });
  }

  public openActionPickerDialog(businessClass: string, listName: string, type?: ReadonlyArray<LMActionPickerActionType>, excludedActions?: ReadonlyArray<string>): Observable<LMActionSpec[]> {
    let actionPickerComponent: LMActionPickerComponent;
    const returnQuery = new Subject<LMActionSpec[]>();

    const closeModalWith = (result) => {
      returnQuery.next(result);
      returnQuery.complete();
      dialogRef.close(true);
    };

    const id = "lm-action-picker-dialog";

    let pickerTitleType = Soho.Locale.translate("Actions");
  
    if (type.includes("navigation")) {
      pickerTitleType = Soho.Locale.translate("Navigations");
    }

    const title = this.lmFormatUtilService.formatMessage(
      Soho.Locale.translate("Action_Picker_SelectAction"),
      [ pickerTitleType, businessClass ]
    );

    const attributes: SohoAttributesArray = [
      {
        name: "data-automation-id",
        value: "lm-action-picker-dialog",
      },
    ];

    const options: SohoModalOptions = {
      fullsize: "responsive",
      showCloseBtn: false,
      attributes,
    };

    const buttons: Array<SohoModalButton> = [
      {
        id: this.lmUniqueIdService.uniqueId([ id, LMDialogButtonKey.CANCEL ]),
        text: Soho.Locale.translate(LMDialogButtonKey.CANCEL),
        click: () => closeModalWith(null),
      },
      {
        id: this.lmUniqueIdService.uniqueId([ id, LMDialogButtonKey.OK ]),
        text: Soho.Locale.translate(LMDialogButtonKey.OK),
        click: () => closeModalWith(actionPickerComponent.selectedActions),
        isDefault: true,
      },
    ];

    const dialogRef: SohoModalDialogRef<LMActionPickerComponent> =
      this.modalService
        .modal(LMActionPickerComponent)
        .title(title)
        .options(options)
        .buttons(buttons)
        .isAlert(false)
        .breakpoint("phablet")
        .apply((comp: LMActionPickerComponent) => {
          actionPickerComponent = comp;
          comp.getActionSpecs(businessClass, listName, type, excludedActions);
        })
        .beforeOpen(() => {
          this.lmBusyIndicatorService.showBusyIndicator();
          return true;
        })
        .afterClose(() => {
          this.destroyer$.next();
          this.destroyer$.complete();
          this.lmBusyIndicatorService.hideBusyIndicator();
          return true;
        })
        .open();

    return returnQuery.asObservable();
  }

  private requestActionSpec(businessClass: string, listName: string): Observable<LMActionSpecResponse> {
    let url = `${this.LMUrlUtilService.getBaseUrl()}/list/${businessClass}.${listName}`;
    url = this.LMUrlUtilService.propagateSessionKeys(url, "?");

    // return cache if the same request url
    if (url === this.previousUrl) {
      return this.cache$;
    }
    this.previousUrl = url;

    const params: LMUrlParams = {
      requestType: "getActionSpecs",
    };

    return this.http.post<LMActionSpecResponse>(url, params).pipe(
      shareReplay(1),
      catchError((error) => {
        this.lmLoggerService.error(error);
        return throwError(error || "Server error");
      })
    );
  }
}
